import type { Company, Contact } from "@/components/atomic-crm/types";
import { formatBillingDate } from "@/modules/billing/billingDisplayUtils";
import {
  formatBillToNameLine,
  resolveBillToDisplay,
} from "@/modules/billing/billingUtils";
import {
  resolveInvoiceBalanceDue,
  resolveInvoiceDisplayTotals,
  type InvoiceDisplaySource,
} from "@/modules/billing/invoiceDisplayTotals";
import { type InvoiceLineDraft } from "@/modules/billing/invoiceLineUtils";
import { resolveInvoiceStatusRibbon } from "@/modules/billing/invoiceStatusRibbon";
import { cn } from "@/lib/utils";
import {
  invoiceDocumentArticleClass,
  invoiceDocumentInnerClass,
  invoiceDocumentOuterClass,
} from "@/modules/billing/invoiceDocumentLayout";
import { processingFeeRateLabel } from "../../../supabase/functions/_shared/processingFees.ts";

const formatMoney = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);

export type CommercialDocumentVariant = "invoice" | "proposal";

type InvoiceDocumentPreviewProps = {
  variant?: CommercialDocumentVariant;
  organizationName: string;
  organizationWebsite?: string | null;
  organizationAddress?: string | null;
  invoiceNumber?: string;
  status?: string;
  issueDate: string;
  dueDate: string;
  /** Shown as “Valid until” when variant is proposal. */
  validUntil?: string | null;
  terms: string;
  /** Short letter / explanation (proposal simple quote). */
  coverMessage?: string | null;
  /** Payment schedule note below totals (proposal). */
  paymentNotes?: string | null;
  /** Gross-up transfer fee row (invoices only by default). */
  showTransferFee?: boolean;
  company?: Company | null;
  contact?: Contact | null;
  /** Addresses this send goes to; replaces the default billing email line. */
  billToEmail?: string | null;
  lines: InvoiceLineDraft[];
  termsAndConditions?: string | null;
  subtotal?: number;
  discountAmount?: number;
  feeAmount?: number;
  total?: number;
  /** Shown in Balance Due row; defaults to total minus amount paid when invoice is set. */
  balanceDue?: number;
  /** When set, subscription invoices skip transfer-fee gross-up for display totals. */
  invoice?: InvoiceDisplaySource | null;
  /** Fee basis when there is no saved invoice (or it changed unsaved). */
  feeMethod?: "card" | "bank";
  className?: string;
  /** After proposal hero — line items, totals, and payment only. */
  layout?: "full" | "lines-only";
};

const formatQuantity = (quantity: number) =>
  Number.isInteger(quantity) ? String(quantity) : quantity.toFixed(2);

const InvoiceLineItemsBlock = ({
  lines,
}: {
  lines: InvoiceLineDraft[];
}) => (
  <>
    <div className="hidden overflow-x-auto rounded-md border sm:block">
      <table className="invoice-doc-table w-full min-w-[280px] table-fixed text-sm">
        <colgroup>
          <col />
          <col style={{ width: "3.5rem" }} />
          <col style={{ width: "5rem" }} />
          <col style={{ width: "5.5rem" }} />
        </colgroup>
        <thead>
          <tr className="bg-slate-700 text-left text-xs uppercase tracking-wide text-white">
            <th className="px-3 py-2.5 font-medium">Item & Description</th>
            <th className="px-1 py-2.5 text-center font-medium">Qty</th>
            <th className="px-1 py-2.5 text-center font-medium">Rate</th>
            <th className="px-1 py-2.5 text-center font-medium">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.length === 0 ? (
            <tr>
              <td colSpan={4} className="px-3 py-8 text-center text-slate-400">
                No line items yet
              </td>
            </tr>
          ) : (
            lines.map((line) => (
              <tr key={line.key} className="border-t">
                <td className="px-3 py-3 align-top">
                  <div className="font-medium text-foreground">
                    {line.title || "—"}
                  </div>
                  {line.item_detail ? (
                    <div className="mt-1 line-clamp-3 whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
                      {line.item_detail}
                    </div>
                  ) : null}
                </td>
                <td className="px-1 py-3 text-center align-middle tabular-nums">
                  {formatQuantity(line.quantity)}
                </td>
                <td className="px-1 py-3 text-center align-middle tabular-nums">
                  {formatMoney(line.unit_price)}
                </td>
                <td className="px-1 py-3 text-center align-middle font-medium tabular-nums">
                  {formatMoney(line.quantity * line.unit_price)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>

    <div className="divide-y rounded-md border bg-white sm:hidden">
      {lines.length === 0 ? (
        <p className="px-3 py-8 text-center text-sm text-slate-400">
          No line items yet
        </p>
      ) : (
        lines.map((line) => (
          <div key={line.key} className="px-3 py-3">
            <p className="text-sm font-semibold leading-snug text-foreground">
              {line.title || "—"}
            </p>
            {line.item_detail ? (
              <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                {line.item_detail}
              </p>
            ) : null}
            <div className="mt-2 flex items-center justify-between gap-2 text-sm">
              <span className="text-muted-foreground tabular-nums">
                {formatQuantity(line.quantity)} × {formatMoney(line.unit_price)}
              </span>
              <span className="font-semibold tabular-nums">
                {formatMoney(line.quantity * line.unit_price)}
              </span>
            </div>
          </div>
        ))
      )}
    </div>
  </>
);

const InvoiceTotalsBlock = ({
  subtotal,
  discountAmount,
  includeTransferFee,
  feeAmount,
  resolvedFeeMethod,
  total,
  balanceDue,
  compact,
}: {
  subtotal: number;
  discountAmount: number;
  includeTransferFee: boolean;
  feeAmount: number;
  resolvedFeeMethod: "card" | "bank";
  total: number;
  balanceDue: number;
  compact?: boolean;
}) => (
  <div className={cn("flex justify-end", compact ? "mt-3" : "mt-4")}>
    <div className="w-full max-w-none text-sm sm:max-w-[18rem]">
      <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-x-4">
        <span className="flex min-h-8 items-center text-slate-600">
          Subtotal
        </span>
        <span className="flex min-h-8 items-center justify-end tabular-nums">
          {formatMoney(subtotal)}
        </span>
        {discountAmount > 0 ? (
          <>
            <span className="flex min-h-8 items-center text-slate-600">
              Discount
            </span>
            <span className="flex min-h-8 items-center justify-end tabular-nums text-red-600">
              -{formatMoney(discountAmount)}
            </span>
          </>
        ) : null}
        {includeTransferFee && feeAmount > 0 ? (
          <>
            <span className="flex min-h-8 items-center text-slate-600">
              Transfer Fee{" "}
              <span className="ml-1 text-2xs font-normal text-slate-400">
                ({processingFeeRateLabel(resolvedFeeMethod)})
              </span>
            </span>
            <span className="flex min-h-8 items-center justify-end tabular-nums">
              {formatMoney(feeAmount)}
            </span>
          </>
        ) : null}
        <div className="col-span-2 my-1 border-t border-slate-200" />
        <span className="flex min-h-8 items-center text-base font-semibold">
          Total
        </span>
        <span className="flex min-h-8 items-center justify-end text-base font-semibold tabular-nums">
          {formatMoney(total)}
        </span>
        {!compact || Math.abs(balanceDue - total) > 0.009 ? (
          <>
            <span className="flex min-h-8 items-center font-semibold">
              Balance due
            </span>
            <span className="flex min-h-8 items-center justify-end font-semibold tabular-nums">
              {formatMoney(balanceDue)}
            </span>
          </>
        ) : null}
      </div>
    </div>
  </div>
);

export const InvoiceDocumentPreview = ({
  variant = "invoice",
  organizationName,
  organizationWebsite,
  organizationAddress,
  invoiceNumber = "DRAFT",
  status = "draft",
  issueDate,
  dueDate,
  validUntil,
  terms,
  coverMessage,
  paymentNotes,
  showTransferFee,
  company,
  contact,
  billToEmail,
  lines,
  termsAndConditions,
  subtotal: subtotalOverride,
  discountAmount: discountOverride,
  feeAmount: feeOverride,
  total: totalOverride,
  balanceDue: balanceDueOverride,
  invoice,
  feeMethod,
  className,
  layout = "full",
}: InvoiceDocumentPreviewProps) => {
  const linesOnly = layout === "lines-only";
  const isProposal = variant === "proposal";
  const resolvedFeeMethod =
    feeMethod ?? (invoice?.fee_method === "bank" ? "bank" : "card");
  const discountAmount = discountOverride ?? 0;
  const discountPercent =
    subtotalOverride != null && subtotalOverride > 0 && discountAmount > 0
      ? (discountAmount / subtotalOverride) * 100
      : 0;
  const calculated = resolveInvoiceDisplayTotals(
    invoice,
    lines,
    discountPercent,
    resolvedFeeMethod,
  );
  const subtotal = subtotalOverride ?? calculated.subtotal;
  const includeTransferFee = showTransferFee ?? !isProposal;
  const feeAmount = includeTransferFee
    ? (feeOverride ?? calculated.feeAmount)
    : 0;
  const total =
    totalOverride ??
    (includeTransferFee
      ? calculated.total
      : Math.max(calculated.subtotal - discountAmount, 0));
  const balanceDue =
    balanceDueOverride ??
    (invoice ? resolveInvoiceBalanceDue(invoice, { total }) : total);
  const billToDisplay = resolveBillToDisplay(company, contact);
  const billToEmailLine =
    billToEmail === undefined ? billToDisplay.email : billToEmail?.trim();
  const billToNameLine = formatBillToNameLine(company, contact);

  const statusRibbon = resolveInvoiceStatusRibbon({
    status,
    due_date: dueDate,
  });
  const statusLabel =
    statusRibbon?.label ??
    (status === "draft"
      ? "Draft"
      : status === "accepted"
        ? "Accepted"
        : status.replace(/_/g, " "));

  const docTitle = isProposal ? "PROPOSAL" : "INVOICE";
  const docNumberLabel = isProposal ? "Proposal#" : "Invoice#";

  return (
    <div className={cn(invoiceDocumentOuterClass, className)}>
      <article className={invoiceDocumentArticleClass}>
        {statusRibbon && !(linesOnly && isProposal) ? (
          <div
            className={cn(
              "pointer-events-none absolute -left-6 top-4 z-10 w-28 -rotate-45 py-1 text-center text-3xs font-semibold uppercase tracking-wider text-white sm:-left-10 sm:top-5 sm:w-36 sm:text-2xs",
              statusRibbon.className,
            )}
          >
            {statusRibbon.label}
          </div>
        ) : null}

        <div
          className={cn(
            invoiceDocumentInnerClass,
            linesOnly && "p-4 sm:p-6",
          )}
        >
          {!linesOnly ? (
          <>
          <div className="flex flex-col gap-6 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-8">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                {docTitle}
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                {docNumberLabel} {invoiceNumber}
              </p>
              <div className="mt-6 sm:mt-8">
                <p className="text-2xs font-semibold uppercase tracking-wider text-slate-500">
                  Balance Due
                </p>
                <p className="text-2xl font-bold tabular-nums text-slate-900 sm:text-4xl">
                  {formatMoney(balanceDue)}
                </p>
              </div>
            </div>
            <div className="min-w-0 text-left text-sm sm:min-w-[200px] sm:text-right">
              <p className="text-base font-semibold text-slate-900">
                {organizationName}
              </p>
              {organizationAddress ? (
                <p className="mt-1 whitespace-pre-line text-slate-600">
                  {organizationAddress}
                </p>
              ) : null}
              {organizationWebsite ? (
                <p className="mt-1 text-blue-600">{organizationWebsite}</p>
              ) : null}
              <p className="mt-3 inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium capitalize text-slate-700">
                {statusLabel}
              </p>
            </div>
          </div>

          {coverMessage?.trim() ? (
            <div className="mt-6 rounded-md border border-slate-200 bg-slate-50/80 p-4 text-sm leading-relaxed text-slate-700">
              <p className="whitespace-pre-wrap">{coverMessage.trim()}</p>
            </div>
          ) : null}

          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-slate-500">
                  {isProposal ? "Proposal date" : "Invoice Date"}
                </p>
                <p className="font-medium">{formatBillingDate(issueDate)}</p>
              </div>
              <div>
                <p className="text-slate-500">Terms</p>
                <p className="font-medium">{terms}</p>
              </div>
              {isProposal && validUntil ? (
                <div>
                  <p className="text-slate-500">Valid until</p>
                  <p className="font-medium">{formatBillingDate(validUntil)}</p>
                </div>
              ) : (
                <div>
                  <p className="text-slate-500">Due Date</p>
                  <p className="font-medium">{formatBillingDate(dueDate)}</p>
                </div>
              )}
            </div>
            <div className="text-sm">
              <p className="mb-2 font-semibold">Bill To</p>
              {billToNameLine ? (
                <p className="font-medium text-blue-700">{billToNameLine}</p>
              ) : null}
              {billToDisplay.addressLines.map((line) => (
                <p key={line} className="text-slate-600">
                  {line}
                </p>
              ))}
              {billToEmailLine ? (
                <p className="text-slate-600">{billToEmailLine}</p>
              ) : null}
              {billToDisplay.phone ? (
                <p className="text-slate-600">{billToDisplay.phone}</p>
              ) : null}
              {!billToNameLine ? (
                <p className="text-slate-400">Select a client</p>
              ) : null}
            </div>
          </div>
          </>
          ) : null}

          {linesOnly ? (
            <div className="mb-4 rounded-md border border-slate-200 bg-slate-50/80 p-3 text-sm">
              <p className="text-2xs font-semibold uppercase tracking-wider text-slate-500">
                Bill to
              </p>
              {billToNameLine ? (
                <p className="mt-1 font-medium text-slate-900">{billToNameLine}</p>
              ) : null}
              {billToDisplay.addressLines.slice(0, 2).map((line) => (
                <p key={line} className="text-slate-600">
                  {line}
                </p>
              ))}
              {billToEmailLine ? (
                <p className="break-all text-slate-600">{billToEmailLine}</p>
              ) : null}
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                <span>
                  {isProposal ? "Valid until" : "Due"}{" "}
                  <span className="font-medium text-slate-900">
                    {formatBillingDate(
                      isProposal && validUntil ? validUntil : dueDate,
                    )}
                  </span>
                </span>
                <span>
                  Terms{" "}
                  <span className="font-medium text-slate-900">{terms}</span>
                </span>
              </div>
            </div>
          ) : null}

          <div className={linesOnly ? "mt-0" : "mt-8"}>
            <InvoiceLineItemsBlock lines={lines} />
          </div>

          <InvoiceTotalsBlock
            subtotal={subtotal}
            discountAmount={discountAmount}
            includeTransferFee={includeTransferFee}
            feeAmount={feeAmount}
            resolvedFeeMethod={resolvedFeeMethod}
            total={total}
            balanceDue={balanceDue}
            compact={linesOnly}
          />

          {paymentNotes?.trim() ? (
            <div className="mt-6 rounded-md border border-slate-200 bg-slate-50/60 p-4">
              <p className="text-2xs font-semibold uppercase tracking-wider text-slate-500">
                Payment
              </p>
              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-600">
                {paymentNotes.trim()}
              </p>
            </div>
          ) : null}

          {termsAndConditions?.trim() ? (
            <div className="mt-8 border-t pt-6">
              <p className="text-2xs font-semibold uppercase tracking-wider text-slate-500">
                Terms & Conditions
              </p>
              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-600">
                {termsAndConditions.trim()}
              </p>
            </div>
          ) : null}
        </div>
      </article>
    </div>
  );
};
